/**
 * Response Handler - Logique commune de gestion des réponses
 * Module partagé entre frontend (api-router.js) et workers (worker-fetch.js, etc.)
 * 
 * Responsabilités:
 * - Décoder les réponses (JSON, texte, etc.)
 * - Valider et classer les erreurs HTTP
 * - Mapper les codes de statut standardisés
 * - Enrichir les réponses avec les métadonnées
 * 
 * Utilisation en frontend:
 * ```js
 * import { parseResponse, classifyErrorType } from '../commons/response-handler.js';
 * const parsed = await parseResponse(response);
 * const errorType = classifyErrorType(response.status, response.headers);
 * ```
 * 
 * Utilisation en worker:
 * ```js
 * import { parseResponse, isResponseOk } from './response-handler.js';
 * const data = await parseResponse(response);
 * if (isResponseOk(response.status)) { ... }
 * ```
 */

import { STANDARD_STATUS_CODES } from './circuit-breaker-constants-core.js';

/**
 * Codes de statut HTTP standardisés pour classification d'erreurs
 * Aligné avec circuit-breaker-constants-core.js
 */
const STATUS_CODES = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  TIMEOUT: 408,
  TOO_MANY_REQUESTS: 429,
  SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
};

/**
 * Parser la réponse en fonction de son type de contenu
 * Gère JSON, texte, ou valeur par défaut
 * 
 * @param {Response} response - L'objet Response de fetch
 * @returns {Promise<Object|string|null>} Les données parsées
 * 
 * @example
 * const response = await fetch('/api/config');
 * const data = await parseResponse(response);
 * // Returns: { languages: [...] } ou texte ou null
 */
export async function parseResponse(response) {
  if (!response) {
    return null;
  }

  try {
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else if (contentType && (contentType.includes('text/') || contentType.includes('text/plain'))) {
      return await response.text();
    } else {
      // Essayer JSON par défaut, sinon texte
      try {
        return await response.json();
      } catch {
        return await response.text();
      }
    }
  } catch (error) {
    console.error('Error parsing response:', error);
    return null;
  }
}

/**
 * Vérifier si une réponse est "ok" (2xx)
 * 
 * @param {number|Response} statusOrResponse - Code de statut ou objet Response
 * @returns {boolean} true si 2xx, false sinon
 * 
 * @example
 * if (isResponseOk(response)) {
 *   const data = await parseResponse(response);
 * }
 */
export function isResponseOk(statusOrResponse) {
  const status = typeof statusOrResponse === 'number' 
    ? statusOrResponse 
    : statusOrResponse?.status;

  return status >= 200 && status < 300;
}

/**
 * Classifier le type d'erreur basé sur le code de statut et les headers
 * Utilise la classification standardisée du circuit-breaker
 * 
 * @param {number} status - Code de statut HTTP
 * @param {Headers|Object} headers - Headers de la réponse (optionnel)
 * @param {string} errorMessage - Message d'erreur (optionnel)
 * @returns {string} Type d'erreur: 'auth', 'connectivity', 'timeout', 'quota', 'service', 'unknown'
 * 
 * @example
 * const errorType = classifyErrorType(403);
 * // Returns: 'auth'
 * 
 * @example
 * const errorType = classifyErrorType(429);
 * // Returns: 'quota'
 */
export function classifyErrorType(status, headers = null, errorMessage = '') {
  // Vérifier X-API-Status (header standardisé des workers)
  if (headers && typeof headers.get === 'function') {
    const apiStatus = headers.get('X-API-Status');
    if (apiStatus) {
      const apiStatusCode = parseInt(apiStatus);
      if (apiStatusCode === STANDARD_STATUS_CODES.AUTH_ERROR || apiStatusCode === 403) {
        return 'auth';
      }
      if (apiStatusCode === STANDARD_STATUS_CODES.REQUEST_TIMEOUT) {
        return 'timeout';
      }
      if (apiStatusCode === STANDARD_STATUS_CODES.QUOTA_ERROR) {
        return 'quota';
      }
      if (apiStatusCode >= STANDARD_STATUS_CODES.SERVER_ERROR) {
        return 'service';
      }
    }
  }

  // Vérifier X-Error-Type (header optionnel pour erreurs explicites)
  if (headers && typeof headers.get === 'function') {
    const errorTypeHeader = headers.get('X-Error-Type');
    if (errorTypeHeader && ['auth', 'connectivity', 'timeout', 'quota', 'service'].includes(errorTypeHeader)) {
      return errorTypeHeader;
    }
  }

  // Classification basée sur le code de statut standard HTTP
  switch (status) {
    case STATUS_CODES.UNAUTHORIZED:
    case STATUS_CODES.FORBIDDEN:
      return 'auth';

    case STATUS_CODES.TIMEOUT:
    case STATUS_CODES.GATEWAY_TIMEOUT:
      return 'timeout';

    case STATUS_CODES.TOO_MANY_REQUESTS:
      return 'quota';

    case STATUS_CODES.SERVER_ERROR:
    case STATUS_CODES.SERVICE_UNAVAILABLE:
      return 'service';

    case STATUS_CODES.BAD_REQUEST:
    case STATUS_CODES.NOT_FOUND:
    case STATUS_CODES.CONFLICT:
      return 'unknown'; // Erreur client (ne devrait pas être traitée comme défaillance service)

    default:
      // Vérifier le message d'erreur pour des indices supplémentaires
      const msgLower = (errorMessage || '').toLowerCase();
      if (msgLower.includes('timeout') || msgLower.includes('abort')) {
        return 'timeout';
      }
      if (msgLower.includes('unauthorized') || msgLower.includes('forbidden')) {
        return 'auth';
      }
      if (msgLower.includes('quota') || msgLower.includes('rate')) {
        return 'quota';
      }
      if (msgLower.includes('network') || msgLower.includes('dns')) {
        return 'connectivity';
      }

      return 'unknown';
  }
}

/**
 * Extraire un message d'erreur lisible depuis une réponse
 * Cherche dans plusieurs endroits: data.error, data.message, body du texte, etc.
 * 
 * @param {Object|string|null} data - Données parsées de la réponse
 * @param {number} status - Code de statut HTTP (pour fallback)
 * @returns {string} Message d'erreur
 * 
 * @example
 * const response = await fetch('/api/invalid');
 * const data = await parseResponse(response);
 * const message = extractErrorMessage(data, response.status);
 * // Returns: "Invalid request" ou "HTTP 400"
 */
export function extractErrorMessage(data, status = 500) {
  if (!data) {
    return `HTTP ${status}`;
  }

  // Essayer plusieurs propriétés communes
  if (typeof data === 'string') {
    return data;
  }

  if (typeof data === 'object') {
    return (
      data.error ||
      data.message ||
      data.error_description ||
      data.errorMessage ||
      data.msg ||
      `HTTP ${status}`
    );
  }

  return `HTTP ${status}`;
}

/**
 * Enrichir une réponse parsée avec les métadonnées
 * Ajoute timestamps, headers, etc. pour debug/logging
 * 
 * @param {Object|string|null} data - Données parsées
 * @param {Response} response - Réponse originale
 * @param {Object} metadata - Métadonnées additionnelles (optionnel)
 * @returns {Object} Réponse enrichie
 * 
 * @example
 * const response = await fetch('/api/config');
 * const data = await parseResponse(response);
 * const enriched = enrichResponse(data, response, { endpoint: '/api/config' });
 * // Returns: { data, timestamp, status, headers, endpoint, ... }
 */
export function enrichResponse(data, response, metadata = {}) {
  const enriched = {
    data,
    status: response.status,
    timestamp: new Date().toISOString(),
    ...metadata
  };

  // Ajouter les headers pertinents pour debug
  if (response.headers) {
    enriched.headers = {
      'content-type': response.headers.get('content-type'),
      'x-api-status': response.headers.get('x-api-status'),
      'x-error-type': response.headers.get('x-error-type'),
      'x-ratelimit-remaining': response.headers.get('x-ratelimit-remaining')
    };
  }

  return enriched;
}

/**
 * Créer un objet d'erreur standardisé pour les handlers
 * Utile pour propager les erreurs avec contexte
 * 
 * @param {string} message - Message d'erreur
 * @param {number} status - Code de statut HTTP
 * @param {string} errorType - Type d'erreur ('auth', 'connectivity', etc.)
 * @param {Object} context - Contexte additionnel {endpoint, workerName, etc.}
 * @returns {Error} Erreur enrichie
 * 
 * @example
 * throw createStandardError(
 *   'Service not available',
 *   503,
 *   'service',
 *   { endpoint: '/api/ia/text', workerName: 'IA-Proxy' }
 * );
 */
export function createStandardError(message, status, errorType, context = {}) {
  const error = new Error(message);
  error.name = 'StandardAPIError';
  error.status = status;
  error.errorType = errorType;
  error.context = context;
  error.timestamp = new Date().toISOString();

  return error;
}

/**
 * Valider que la réponse contient des données attendues
 * Utile pour les validations métier
 * 
 * @param {Object|null} data - Données à valider
 * @param {string|Array<string>} expectedFields - Champs attendus
 * @returns {Object} {isValid: boolean, missingFields: string[]}
 * 
 * @example
 * const data = await parseResponse(response);
 * const validation = validateResponseSchema(data, ['id', 'name', 'email']);
 * if (!validation.isValid) {
 *   console.error('Missing fields:', validation.missingFields);
 * }
 */
export function validateResponseSchema(data, expectedFields = []) {
  if (!data || typeof data !== 'object') {
    return { isValid: false, missingFields: expectedFields };
  }

  const fields = Array.isArray(expectedFields) ? expectedFields : [expectedFields];
  const missingFields = fields.filter(field => !(field in data));

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}

/**
 * Créer une réponse d'erreur HTTP standardisée
 * Utile pour les workers qui doivent envoyer des réponses JSON
 * 
 * @param {string} message - Message d'erreur
 * @param {number} status - Code de statut HTTP (défaut: 500)
 * @param {Object} headers - Headers additionnels
 * @returns {Response} Réponse d'erreur
 * 
 * @example
 * // Utilisation en worker
 * const response = createErrorResponse(
 *   'Invalid configuration',
 *   400,
 *   { 'X-Error-Type': 'unknown' }
 * );
 * return response;
 */
export function createErrorResponse(message, status = 500, headers = {}) {
  return new Response(
    JSON.stringify({
      error: message,
      status,
      timestamp: new Date().toISOString()
    }),
    {
      status,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      }
    }
  );
}

/**
 * Créer une réponse de succès HTTP standardisée
 * Utile pour les workers qui doivent envoyer des réponses JSON
 * 
 * @param {Object|Array|string} data - Données à envoyer
 * @param {number} status - Code de statut HTTP (défaut: 200)
 * @param {Object} headers - Headers additionnels
 * @returns {Response} Réponse de succès
 * 
 * @example
 * // Utilisation en worker
 * const response = createSuccessResponse({ id: 1, name: 'Test' }, 200);
 * return response;
 */
export function createSuccessResponse(data, status = 200, headers = {}) {
  return new Response(
    JSON.stringify(data),
    {
      status,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      }
    }
  );
}

/**
 * Convertit les données parsées (de routedFetch ou workerFetch) en un objet Response-like
 * Utility pour les loaders (handlebars-loader) qui s'attendent à un Response-like
 * 
 * Utilisation en frontend:
 * ```js
 * import { createResponseWrapper } from '../commons-shared/response-handler.js';
 * const responseLike = await createResponseWrapper(
 *   async () => routedFetch(url, options)
 * );
 * ```
 * 
 * Utilisation en worker:
 * ```js
 * import { createResponseWrapper } from '../commons-shared/response-handler.js';
 * const responseLike = await createResponseWrapper(
 *   async () => workerFetch(endpoint, options, env)
 * );
 * ```
 * 
 * @param {Function} fetchFn - Fonction async qui retourne des données parsées
 * @returns {Promise<Object>} - Objet Response-like avec ok, status, json(), text(), clone()
 */
export async function createResponseWrapper(fetchFn) {
  try {
    const data = await fetchFn();
    
    // Créer un objet Response-like pour les loaders
    return {
      ok: true,
      status: 200,
      statusText: 'OK',
      headers: new Map([['content-type', 'application/json']]),
      json: async () => data,
      text: async () => JSON.stringify(data),
      clone: function() { return this; }
    };
  } catch (error) {
    // Retourner un objet Response-like avec erreur
    return {
      ok: false,
      status: error.status || 500,
      statusText: error.statusText || 'Error',
      headers: new Map([['content-type', 'application/json']]),
      json: async () => { throw error; },
      text: async () => error.message,
      clone: function() { return this; }
    };
  }
}

/**
 * Export par défaut pour commodité
 */
export default {
  parseResponse,
  isResponseOk,
  classifyErrorType,
  extractErrorMessage,
  enrichResponse,
  createStandardError,
  validateResponseSchema,
  createErrorResponse,
  createSuccessResponse,
  createResponseWrapper
};
